import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";

type Theme = "light" | "dark";

const NAV = [
  { to: "/schedule", label: "Schedule" },
  { to: "/travel", label: "Travel" },
  { to: "/rsvp", label: "RSVP" },
  { to: "/games", label: "Games" },
  { to: "/faq", label: "Q&A" },
];

export function Header({ theme }: { theme: Theme }) {
  const dark = theme === "dark";
  const [open, setOpen] = useState(false);

  return (
    <header className="px-6 py-6">
      <div className="mx-auto flex max-w-6xl items-center justify-between">
        <Link
          to="/"
          aria-label="Karishma & Ben — Home"
          className="inline-block"
        >
          <img
            src={dark ? "/kb-full-beige-no-words.svg" : "/kb-blue-green.svg"}
            alt="Karishma & Ben"
            className="h-16 w-auto"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden gap-8 text-base font-semibold uppercase tracking-widest md:flex">
          {NAV.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              label={item.label}
              dark={dark}
            />
          ))}
        </nav>

        {/* Mobile trigger */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className={`-mr-2 p-2 md:hidden ${dark ? "text-cream" : "text-pine"}`}
        >
          <span aria-hidden="true" className="block h-px w-7 bg-current" />
          <span aria-hidden="true" className="mt-2 block h-px w-7 bg-current" />
          <span aria-hidden="true" className="mt-2 block h-px w-7 bg-current" />
        </button>
      </div>

      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </header>
  );
}

/** Full-screen cream overlay with the nav centered in large serif. */
function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Lock background scroll and wire Escape while the menu is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <div
      aria-hidden={!open}
      className={`fixed inset-0 z-50 flex flex-col bg-cream transition-opacity duration-300 md:hidden ${
        open
          ? "pointer-events-auto opacity-100"
          : "pointer-events-none opacity-0"
      }`}
    >
      <div className="flex items-center justify-between px-6 py-6">
        <Link to="/" onClick={onClose} aria-label="Karishma & Ben — Home">
          <img
            src="/kb-blue-green.svg"
            alt="Karishma & Ben"
            className="h-14 w-auto"
          />
        </Link>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="-mr-2 p-2 text-pine"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1}
            className="h-7 w-7"
          >
            <path d="M5 5l14 14M19 5L5 19" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <nav className="flex flex-1 flex-col items-center justify-center gap-9">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) =>
              `font-serif text-4xl tracking-wide transition-colors ${
                isActive ? "text-midnight-teal" : "text-pine"
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div
        aria-hidden="true"
        className="pb-16 text-center text-xl text-dusty-rose"
      >
        ❃
      </div>
    </div>
  );
}

function NavItem({
  to,
  label,
  dark,
}: {
  to: string;
  label: string;
  dark: boolean;
}) {
  // Links sit at full highlight brightness all the time; the affordance is a
  // left-to-right swooping underline on hover/focus (and persistent on the
  // active page, since colour no longer signals it).
  const color = dark ? "text-cream" : "text-midnight-teal";
  const underline = dark ? "bg-blush" : "bg-dusty-rose";

  return (
    <NavLink
      to={to}
      className={`group relative inline-block outline-none ${color}`}
    >
      {({ isActive }) => (
        <>
          {label}
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute -bottom-1 left-0 h-px w-full origin-left rounded-full ${underline} transition-transform duration-500 ease-out group-hover:scale-x-100 group-focus-visible:scale-x-100 ${
              isActive ? "scale-x-100" : "scale-x-0"
            }`}
          />
        </>
      )}
    </NavLink>
  );
}
